import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Box, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser } from '@/lib/api/auth';
import { getClientById } from '@/lib/api/clients';
import {
  getQuestionnaireCustomQuestions,
  getQuestionnaireById,
  getQuestionnaireInstanceResponse,
  getQuestionnaireInstanceTotalQuestionCount,
  getQuestionnaireResponses,
  type GetQuestionnaireResponsesResponse,
  type QuestionnaireCustomQuestion,
  type QuestionnaireResponseItem,
  type QuestionnaireInstanceResponse,
} from '@/lib/api/questionnaires';
import { QuestionnaireListRow } from '@/components/questionnaires/final-page/QuestionnaireListRow';
import type { QuestionnaireCardData } from '@/components/questionnaires/final-page/types';
import type { JsonQuestion, JsonQuestionnaireSection } from '@/components/questionnaires/json-driven/types';
import { getQuestionKey } from '@/components/questionnaires/json-driven/questionKey';
import { sharedAssignedClientFromClientProfile } from '@/components/questionnaires/json-driven/clientContext';
import { QuestionnaireManualFillDialog } from '@/pages/dashboard/questionnaire-final-detail/sections';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { scheduleReloadForDynamicImportError } from '@/utils/dynamic-import-recovery';

interface ClientQuestionnairesSectionProps {
  clientId: string;
}

type QuestionnaireFilter = 'completed' | 'in-progress' | 'pending';

function normalizeQuestionnaireFilter(value: string | null): QuestionnaireFilter | null {
  const normalized = value?.trim().toLowerCase().replace(/_/g, '-');
  if (!normalized) return null;
  if (normalized === 'completed' || normalized === 'complete') return 'completed';
  if (normalized === 'in-progress' || normalized === 'inprogress') return 'in-progress';
  if (normalized === 'pending' || normalized === 'assigned' || normalized === 'not-started') return 'pending';
  return null;
}

function getQuestionnaireFilterFromSearch(search: string): QuestionnaireFilter | null {
  const params = new URLSearchParams(search);
  return normalizeQuestionnaireFilter(
    params.get('questionnaireStatus') ?? params.get('questionnaireFilter')
  );
}

function matchesQuestionnaireFilter(
  status: QuestionnaireInstanceResponse['status'],
  filter: QuestionnaireFilter
): boolean {
  if (filter === 'completed') return status === 'completed';
  if (filter === 'in-progress') return status === 'in_progress';
  return status === 'pending';
}

type QuestionnaireMeta = {
  estimatedTimeMinutes?: number;
  subtitle?: string;
};

type PrintableQuestionnaireSection = {
  label: string;
  questions: JsonQuestion[];
};

type SubmissionResponseRow = {
  rowKey: string;
  response: QuestionnaireResponseItem;
  submissionIndex: number;
  responseIndex: number;
  submittedAt?: string;
  submittedAtMs: number;
};

type QuestionnaireResponseSource = {
  submittedAt?: string;
  responses?: QuestionnaireResponseItem[] | null;
  submissions?: Array<{ submittedAt?: string; responses?: QuestionnaireResponseItem[] | null }> | null;
};

function getQuestionnaireTitle(questionnaire: QuestionnaireInstanceResponse): string {
  return questionnaire.questionnaireTitle || questionnaire.questionnaireName || '';
}

function stripPrintableTriggerText(value?: string): string | undefined {
  const trimmed = value?.replace(/TRIGGER:\s*[^|]+/gi, '').replace(/\s+/g, ' ').trim();
  return trimmed || undefined;
}

function cleanPrintableSectionLabel(value?: string): string {
  const normalized = value?.replace(/\s+/g, ' ').trim();
  return normalized || 'General';
}

function isAdditionalPrintableQuestion(question: JsonQuestion): boolean {
  const label = cleanPrintableSectionLabel(question.label).toLowerCase();
  return question.isCustom === true || label === 'custom' || label === 'additional questions';
}

function getPrintableQuestionKey(question: JsonQuestion): string {
  const id = question._id || question.questionId || question.externalId;
  if (id) return String(id);
  return `${question.type || 'question'}:${question.order ?? ''}:${question.questionText || ''}`;
}

function normalizeResponseQuestionText(value?: string): string {
  return value?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';
}

function isAnswerProvided(answer: unknown, otherText?: string): boolean {
  if (typeof otherText === 'string' && otherText.trim().length > 0) return true;
  if (answer == null) return false;
  if (Array.isArray(answer)) return answer.length > 0;
  if (typeof answer === 'string') return answer.trim().length > 0;
  if (typeof answer === 'boolean') return true;
  if (typeof answer === 'object') {
    const value = answer as Record<string, unknown>;
    const rawValue = value.value;
    if (Array.isArray(rawValue)) return rawValue.length > 0;
    if (typeof rawValue === 'string') return rawValue.trim().length > 0;
    if (rawValue != null) return true;
    return Object.keys(value).length > 0;
  }
  return true;
}

function isResponseAnswered(row: SubmissionResponseRow): boolean {
  return isAnswerProvided(row.response.answer, row.response.otherText);
}

function isPreferredResponseRow(
  candidate: SubmissionResponseRow,
  existing: SubmissionResponseRow | undefined
): boolean {
  if (!existing) return true;

  const candidateAnswered = isResponseAnswered(candidate);
  const existingAnswered = isResponseAnswered(existing);
  if (candidateAnswered !== existingAnswered) return candidateAnswered;

  if (candidate.submittedAtMs !== existing.submittedAtMs) {
    return candidate.submittedAtMs > existing.submittedAtMs;
  }

  if (candidate.submissionIndex !== existing.submissionIndex) {
    return candidate.submissionIndex > existing.submissionIndex;
  }

  return candidate.responseIndex > existing.responseIndex;
}

function toSubmittedAtMs(value?: string): number {
  const parsed = new Date(value ?? '').getTime();
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

function buildResponseRowsFromSource(source: QuestionnaireResponseSource): SubmissionResponseRow[] {
  const rowsFromSubmissions = (source.submissions ?? []).flatMap((submission, submissionIndex) => {
    const submittedAt = submission.submittedAt ?? source.submittedAt;
    const submittedAtMs = toSubmittedAtMs(submittedAt);

    return (submission.responses ?? []).map((response, responseIndex) => ({
      rowKey: `submission:${submissionIndex}:${responseIndex}`,
      response,
      submissionIndex,
      responseIndex,
      submittedAt,
      submittedAtMs,
    }));
  });

  if (rowsFromSubmissions.length > 0) {
    return rowsFromSubmissions;
  }

  const submittedAt = source.submittedAt;
  const submittedAtMs = toSubmittedAtMs(submittedAt);
  return (source.responses ?? []).map((response, responseIndex) => ({
    rowKey: `response:${responseIndex}`,
    response,
    submissionIndex: 0,
    responseIndex,
    submittedAt,
    submittedAtMs,
  }));
}

function mapCustomQuestionToPrintableQuestion(
  question: QuestionnaireCustomQuestion,
  index: number,
  baseQuestionCount: number
): JsonQuestion {
  const id = question.questionId || question._id || `custom-question-${index + 1}`;
  return {
    _id: id,
    questionId: question.questionId || question._id,
    questionText: question.questionText,
    type: question.type,
    inputType: question.inputType ?? undefined,
    options: question.options ?? undefined,
    placeholder: question.placeholder ?? undefined,
    validation: question.validation ?? undefined,
    medicalConditions: question.medicalConditions,
    gender: question.gender,
    ageMin: question.ageMin,
    ageMax: question.ageMax,
    isConditional: question.isConditional,
    label: 'Additional Questions',
    order:
      typeof question.order === 'number' && Number.isFinite(question.order)
        ? question.order
        : baseQuestionCount + index,
    isActive: true,
    isCustom: true,
  };
}

function buildPrintableSections(
  questionnaireDefinition: JsonQuestionnaireSection | null,
  customQuestions: QuestionnaireCustomQuestion[]
): PrintableQuestionnaireSection[] {
  const definitionQuestions = (questionnaireDefinition?.questions ?? []).filter(
    (question) => question.isActive !== false && typeof question.questionText === 'string' && question.questionText.trim().length > 0
  );
  const customPrintableQuestions = customQuestions
    .map((question, index) => mapCustomQuestionToPrintableQuestion(question, index, definitionQuestions.length))
    .filter((question) => typeof question.questionText === 'string' && question.questionText.trim().length > 0);

  const seenQuestionKeys = new Set<string>();
  const allQuestions = [...definitionQuestions, ...customPrintableQuestions]
    .filter((question) => {
      const key = getPrintableQuestionKey(question);
      if (seenQuestionKeys.has(key)) return false;
      seenQuestionKeys.add(key);
      return true;
    })
    .sort((left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER));

  const baseGroups = new Map<string, PrintableQuestionnaireSection>();
  const additionalQuestions: JsonQuestion[] = [];

  allQuestions.forEach((question) => {
    if (isAdditionalPrintableQuestion(question)) {
      additionalQuestions.push(question);
      return;
    }

    const label = cleanPrintableSectionLabel(question.label);
    const key = label.toLowerCase();
    const existing = baseGroups.get(key);
    if (existing) {
      existing.questions.push(question);
      return;
    }
    baseGroups.set(key, { label, questions: [question] });
  });

  return [
    ...Array.from(baseGroups.values()),
    ...(additionalQuestions.length > 0
      ? [{ label: 'Additional Questions', questions: additionalQuestions }]
      : []),
  ].filter((section) => section.questions.length > 0);
}

function getShareLink(instanceId: string): string {
  return `${window.location.origin}/questionnaire/share/${encodeURIComponent(instanceId)}`;
}

async function copyShareLink(instanceId: string, successMessage: string) {
  try {
    await navigator.clipboard.writeText(getShareLink(instanceId));
    showSuccessToast(successMessage);
  } catch (error) {
    showErrorToast(
      error instanceof Error ? error : new Error('Failed to copy questionnaire link'),
      'Error'
    );
  }
}

function sortQuestionnaires(questionnaires: QuestionnaireInstanceResponse[]) {
  const priority: Record<QuestionnaireInstanceResponse['status'], number> = {
    in_progress: 0,
    pending: 1,
    completed: 2,
  };

  return [...questionnaires].sort((left, right) => {
    const priorityDelta = priority[left.status] - priority[right.status];
    if (priorityDelta !== 0) return priorityDelta;

    const leftDate = new Date(
      left.submittedAt || left.completedAt || left.createdAt || 0
    ).getTime();
    const rightDate = new Date(
      right.submittedAt || right.completedAt || right.createdAt || 0
    ).getTime();

    return rightDate - leftDate;
  });
}

function mapQuestionnaireToCard(
  questionnaire: QuestionnaireInstanceResponse,
  questionnaireMetaById: Map<string, QuestionnaireMeta>
): QuestionnaireCardData {
  const questionnaireMeta = questionnaire.questionnaireId
    ? questionnaireMetaById.get(questionnaire.questionnaireId)
    : undefined;
  const totalQuestionCount = getQuestionnaireInstanceTotalQuestionCount(questionnaire);

  return {
    id: questionnaire.instanceId,
    questionnaireId: questionnaire.questionnaireId,
    title: getQuestionnaireTitle(questionnaire),
    name: getQuestionnaireTitle(questionnaire),
    subtitle: questionnaireMeta?.subtitle,
    description: questionnaireMeta?.subtitle,
    questions: [],
    type: 'core',
    status: 'ready',
    templateQuestionsCount: totalQuestionCount,
    questionsCount: totalQuestionCount,
    estimatedTimeMinutes: questionnaireMeta?.estimatedTimeMinutes,
    createdAt: questionnaire.createdAt,
    updatedAt: questionnaire.submittedAt || questionnaire.completedAt || questionnaire.createdAt,
    isActive: true,
  };
}

function markQuestionnaireInstanceInProgress(
  current: GetQuestionnaireResponsesResponse | undefined,
  instanceId: string
): GetQuestionnaireResponsesResponse | undefined {
  if (!current) return current;

  let didUpdate = false;
  const questionnaires = current.questionnaires.map((questionnaire) => {
    if (questionnaire.instanceId !== instanceId) return questionnaire;
    if (questionnaire.status === 'in_progress' && questionnaire.hasResponse) return questionnaire;

    didUpdate = true;
    return {
      ...questionnaire,
      status: 'in_progress' as const,
      hasResponse: true,
    };
  });

  if (!didUpdate) return current;

  return {
    ...current,
    questionnaires,
    completedCount: questionnaires.filter((questionnaire) => questionnaire.status === 'completed').length,
    pendingCount: questionnaires.filter((questionnaire) => questionnaire.status === 'pending').length,
    inProgressCount: questionnaires.filter((questionnaire) => questionnaire.status === 'in_progress').length,
  };
}

function openQuestionnaireView(
  navigate: ReturnType<typeof useNavigate>,
  questionnaire: QuestionnaireInstanceResponse,
  clientId: string,
  clientName?: string,
) {
  if (questionnaire.status === 'completed' && questionnaire.hasResponse && questionnaire.questionnaireId) {
    const query = new URLSearchParams({ clientId });
    if (clientName?.trim()) {
      query.set('clientName', clientName.trim());
    }
    navigate(
      `/questionnaires/${encodeURIComponent(questionnaire.questionnaireId)}/responses/${encodeURIComponent(questionnaire.instanceId)}?${query.toString()}`
    );
    return;
  }

  window.open(getShareLink(questionnaire.instanceId), '_blank', 'noopener,noreferrer');
}

async function copyQuestionnaireLink(questionnaire: QuestionnaireInstanceResponse) {
  await copyShareLink(questionnaire.instanceId, 'Questionnaire link copied to clipboard');
}

export const ClientQuestionnairesSection: React.FC<ClientQuestionnairesSectionProps> = ({
  clientId,
}) => {
  const user = getCurrentUser();
  const nutritionistId = user?.id ?? undefined;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isDetailsV2QuestionnairesRoute = location.pathname.includes('/details-v2/questionnaires');
  const requestedFilter = React.useMemo(
    () => getQuestionnaireFilterFromSearch(location.search),
    [location.search]
  );
  const [activeFilter, setActiveFilter] = useState<QuestionnaireFilter>(
    requestedFilter ?? (isDetailsV2QuestionnairesRoute ? 'completed' : 'in-progress')
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [exportingInstanceIds, setExportingInstanceIds] = useState<Set<string>>(new Set());
  const [manualFillQuestionnaire, setManualFillQuestionnaire] =
    useState<QuestionnaireInstanceResponse | null>(null);
  const exportingInstanceIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (requestedFilter) {
      setActiveFilter(requestedFilter);
      return;
    }
    if (isDetailsV2QuestionnairesRoute) {
      setActiveFilter('completed');
    }
  }, [isDetailsV2QuestionnairesRoute, requestedFilter]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['questionnaireResponses', clientId, nutritionistId],
    queryFn: () => getQuestionnaireResponses(clientId, nutritionistId),
    enabled: !!clientId,
  });

  const { data: clientDetails } = useQuery({
    queryKey: ['clientById', clientId, 'questionnaire-export'],
    queryFn: () => getClientById(clientId),
    enabled: Boolean(clientId),
    staleTime: 60_000,
  });

  const questionnaires = useMemo(
    () => sortQuestionnaires(data?.questionnaires ?? []),
    [data?.questionnaires]
  );

  // We are removing the multiple individual API calls per questionnaire since the responses API 
  // already provides the necessary data to render the cards (title, questionsCount, status)
  // and we don't need the individual definition (subtitle, estimatedTimeMinutes) enough to 
  // justify making N additional API calls just to render the list.

  const questionnaireMetaById = useMemo(() => {
    const map = new Map<string, QuestionnaireMeta>();
    return map; // Return empty map as we no longer fetch individual definitions for the list
  }, []);

  const questionnaireCards = useMemo(
    () =>
      questionnaires.map((questionnaire) => ({
        questionnaire,
        card: mapQuestionnaireToCard(questionnaire, questionnaireMetaById),
      })),
    [questionnaireMetaById, questionnaires]
  );

  const filterCounts = useMemo(
    () => ({
      completed: questionnaires.filter((questionnaire) => questionnaire.status === 'completed').length,
      'in-progress': questionnaires.filter((questionnaire) => questionnaire.status === 'in_progress').length,
      pending: questionnaires.filter((questionnaire) => questionnaire.status === 'pending').length,
    }),
    [questionnaires]
  );

  const filteredQuestionnaireCards = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return questionnaireCards.filter(({ questionnaire, card }) => {
      const matchesFilter = matchesQuestionnaireFilter(questionnaire.status, activeFilter);

      if (!matchesFilter) return false;
      if (!normalizedQuery) return true;

      const title = getQuestionnaireTitle(questionnaire).toLowerCase();
      const description = card.description?.toLowerCase() ?? '';

      return title.includes(normalizedQuery) || description.includes(normalizedQuery);
    });
  }, [activeFilter, questionnaireCards, searchQuery]);

  const handleExportQuestionnaire = React.useCallback(
    async (questionnaire: QuestionnaireInstanceResponse) => {
      const instanceId = questionnaire.instanceId;
      if (exportingInstanceIdsRef.current.has(instanceId)) return;
      exportingInstanceIdsRef.current.add(instanceId);
      setExportingInstanceIds((previous) => {
        const next = new Set(previous);
        next.add(instanceId);
        return next;
      });

      try {
        const [instanceResponse, questionnaireDefinition, customQuestionsResponse] = await Promise.all([
          getQuestionnaireInstanceResponse(questionnaire.instanceId),
          questionnaire.questionnaireId
            ? getQuestionnaireById(questionnaire.questionnaireId, nutritionistId).catch(() => null)
            : Promise.resolve(null),
          questionnaire.questionnaireId
            ? getQuestionnaireCustomQuestions(questionnaire.questionnaireId, nutritionistId).catch(() => null)
            : Promise.resolve(null),
        ]);

        const responseRows = buildResponseRowsFromSource(instanceResponse);
        const fallbackResponseRows = buildResponseRowsFromSource(questionnaire);
        const exportResponseRows = responseRows.length > 0 ? responseRows : fallbackResponseRows;
        const answeredResponseRows = exportResponseRows.filter(isResponseAnswered);

        if (answeredResponseRows.length === 0) {
          const printableSections = buildPrintableSections(
            questionnaireDefinition,
            customQuestionsResponse?.customQuestions ?? []
          );

          if (printableSections.length === 0) {
            showWarningToast('No questions available', 'This questionnaire has no active questions to export.');
            return;
          }

          const { downloadPrintableQuestionnaireForm } = await import(
            '@/pages/dashboard/questionnaire-final-detail/printableQuestionnaireForm'
          );

          await downloadPrintableQuestionnaireForm({
            title: getQuestionnaireTitle(questionnaire) || questionnaireDefinition?.title || 'Questionnaire',
            questionnaireTypeLabel: questionnaireDefinition?.type === 'custom' ? 'Custom Questionnaire' : 'Core Questionnaire',
            description: stripPrintableTriggerText(questionnaireDefinition?.description),
            clinicLogoUrl: user?.clinicLogoUrl ?? undefined,
            sections: printableSections,
          });

          showSuccessToast('PDF downloaded', 'Printable blank questionnaire PDF downloaded successfully.');
          return;
        }

        const latestAnsweredRow = answeredResponseRows
          .slice()
          .sort((left, right) =>
            left.submittedAtMs === right.submittedAtMs
              ? right.submissionIndex - left.submissionIndex || right.responseIndex - left.responseIndex
              : right.submittedAtMs - left.submittedAtMs
          )[0];

        const responseLookupByQuestionId = new Map<string, SubmissionResponseRow>();
        const responseLookupByQuestionText = new Map<string, SubmissionResponseRow>();

        exportResponseRows.forEach((row) => {
          const responseItem = row.response;
          if (typeof responseItem.questionId === 'string' && responseItem.questionId.trim().length > 0) {
            const key = responseItem.questionId.trim();
            const existing = responseLookupByQuestionId.get(key);
            if (isPreferredResponseRow(row, existing)) {
              responseLookupByQuestionId.set(key, row);
            }
          }

          const questionTextKey = normalizeResponseQuestionText(responseItem.questionText);
          if (questionTextKey) {
            const existing = responseLookupByQuestionText.get(questionTextKey);
            if (isPreferredResponseRow(row, existing)) {
              responseLookupByQuestionText.set(questionTextKey, row);
            }
          }
        });

        const mergedDefinitionQuestions = [
          ...(questionnaireDefinition?.questions ?? []).map((question, index) => ({
            ids: Array.from(new Set([
              getQuestionKey(question),
              question._id,
              question.questionId,
              question.externalId,
            ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
              .map((value) => value.trim()))),
            questionText: question.questionText,
            type: question.type,
            options: question.options,
            order:
              typeof question.order === 'number' && Number.isFinite(question.order)
                ? question.order
                : index,
            isActive: question.isActive !== false,
          })),
          ...((customQuestionsResponse?.customQuestions ?? []).map((question, index) => ({
            ids: Array.from(new Set([
              question.questionId,
              question._id,
            ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
              .map((value) => value.trim()))),
            questionText: question.questionText,
            type: question.type,
            options: question.options,
            order:
              typeof question.order === 'number' && Number.isFinite(question.order)
                ? question.order
                : (questionnaireDefinition?.questions?.length ?? 0) + index,
            isActive: true,
          })) ?? []),
        ]
          .filter((question) => question.isActive && typeof question.questionText === 'string' && question.questionText.trim().length > 0)
          .sort((left, right) => left.order - right.order);

        const seenDefinitionQuestionIds = new Set<string>();
        const orderedDefinitionQuestions = mergedDefinitionQuestions.filter((question) => {
          const ids = question.ids;
          if (ids.length === 0) return true;
          if (ids.some((id) => seenDefinitionQuestionIds.has(id))) return false;
          ids.forEach((id) => seenDefinitionQuestionIds.add(id));
          return true;
        });

        const consumedResponseRowKeys = new Set<string>();
        const sectionItemsFromDefinition = orderedDefinitionQuestions.map((question) => {
          const matchedRow =
            question.ids.map((id) => responseLookupByQuestionId.get(id)).find(Boolean)
            ?? responseLookupByQuestionText.get(normalizeResponseQuestionText(question.questionText));

          if (matchedRow) {
            consumedResponseRowKeys.add(matchedRow.rowKey);
          }

          const matchedResponse = matchedRow?.response;

          return {
            questionText: matchedResponse?.questionText || question.questionText,
            type: matchedResponse?.type || question.type,
            options: question.options,
            answer: matchedResponse?.answer,
            otherText: matchedResponse?.otherText,
            answeredAt: matchedResponse?.answeredAt,
          };
        });

        const unmatchedResponseItems = exportResponseRows
          .filter((row) => {
            if (consumedResponseRowKeys.has(row.rowKey)) return false;
            if (!isResponseAnswered(row)) return false;

            const questionId = row.response.questionId?.trim();
            if (questionId && responseLookupByQuestionId.get(questionId)?.rowKey !== row.rowKey) {
              return false;
            }

            const questionTextKey = normalizeResponseQuestionText(row.response.questionText);
            if (!questionId && questionTextKey && responseLookupByQuestionText.get(questionTextKey)?.rowKey !== row.rowKey) {
              return false;
            }

            return true;
          })
          .map((row) => {
            const responseItem = row.response;
            return {
              questionText: responseItem.questionText,
              type: responseItem.type,
              answer: responseItem.answer,
              otherText: responseItem.otherText,
              answeredAt: responseItem.answeredAt,
            };
          });

        const sectionItems =
          sectionItemsFromDefinition.length > 0
            ? [...sectionItemsFromDefinition, ...unmatchedResponseItems]
            : unmatchedResponseItems;

        const sections = [
          {
            heading: 'Submitted Response',
            submittedAt: latestAnsweredRow?.submittedAt,
            items: sectionItems,
          },
        ];

        const { downloadQuestionnaireResponsePdf } = await import(
          '@/clients/details/shared/utils/questionnaireResponseExportPdf'
        );

        const clientName =
          clientDetails?.client?.fullName?.trim()
          || `${clientDetails?.client?.firstName ?? ''} ${clientDetails?.client?.lastName ?? ''}`.trim()
          || 'Client';

        await downloadQuestionnaireResponsePdf({
          clientName,
          clientEmail: clientDetails?.client?.email || undefined,
          questionnaireTitle: getQuestionnaireTitle(questionnaire) || questionnaireDefinition?.title || 'Questionnaire',
          questionnaireStatus: questionnaire.status,
          clinicLogoUrl: user?.clinicLogoUrl ?? undefined,
          sections,
        });

        showSuccessToast('PDF downloaded', 'Questionnaire response PDF downloaded successfully.');
      } catch (exportError) {
        if (scheduleReloadForDynamicImportError(exportError, {
          storageKey: 'caloriescience:questionnaire-pdf-export-reload-at',
        })) {
          showWarningToast(
            'App update detected',
            'Reloading the latest app files. Please try exporting again after the page reloads.',
          );
          return;
        }
        showErrorToast(exportError, 'Failed to export questionnaire PDF');
      } finally {
        exportingInstanceIdsRef.current.delete(instanceId);
        setExportingInstanceIds((previous) => {
          const next = new Set(previous);
          next.delete(instanceId);
          return next;
        });
      }
    },
    [clientDetails?.client?.email, clientDetails?.client?.firstName, clientDetails?.client?.fullName, clientDetails?.client?.lastName, nutritionistId, user?.clinicLogoUrl]
  );

  const manualFillClientName =
    clientDetails?.client?.fullName?.trim()
    || `${clientDetails?.client?.firstName ?? ''} ${clientDetails?.client?.lastName ?? ''}`.trim()
    || 'Selected client';

  const handleManualFillSubmitted = React.useCallback(
    (instanceId: string) => {
      void queryClient.invalidateQueries({
        queryKey: ['questionnaireResponses', clientId, nutritionistId],
      });

      if (manualFillQuestionnaire?.questionnaireId) {
        const query = new URLSearchParams({ clientId, clientName: manualFillClientName });
        navigate(
          `/questionnaires/${encodeURIComponent(manualFillQuestionnaire.questionnaireId)}/responses/${encodeURIComponent(instanceId)}?${query.toString()}`
        );
      }
    },
    [clientId, manualFillClientName, manualFillQuestionnaire?.questionnaireId, navigate, nutritionistId, queryClient]
  );

  const handleManualFillDraftSaved = React.useCallback(
    (instanceId: string) => {
      queryClient.setQueryData<GetQuestionnaireResponsesResponse>(
        ['questionnaireResponses', clientId, nutritionistId],
        (current) => markQuestionnaireInstanceInProgress(current, instanceId)
      );
      void queryClient.invalidateQueries({
        queryKey: ['questionnaireResponses', clientId, nutritionistId],
      });
    },
    [clientId, nutritionistId, queryClient]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        Loading questionnaires…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-destructive">
        {error instanceof Error ? error.message : 'Failed to load questionnaires.'}
      </div>
    );
  }

  const hasSearchQuery = searchQuery.trim().length > 0;
  const emptyQuestionnaireMessage = (() => {
    if (questionnaireCards.length === 0) {
      return 'No questionnaires assigned to this client as yet.';
    }

    if (activeFilter === 'completed') {
      if (filterCounts.completed === 0) {
        return 'No questionnaires have been completed yet.';
      }
      return hasSearchQuery
        ? 'No completed questionnaires match the current search.'
        : 'No completed questionnaires match the current filter.';
    }

    if (activeFilter === 'in-progress') {
      if (filterCounts['in-progress'] === 0) {
        return 'No questionnaires are currently in progress.';
      }
      return hasSearchQuery
        ? 'No in-progress questionnaires match the current search.'
        : 'No questionnaires match the current filter.';
    }

    if (filterCounts.pending === 0) {
      return 'No pending questionnaires.';
    }

    return hasSearchQuery
      ? 'No pending questionnaires match the current search.'
      : 'No questionnaires match the current filter.';
  })();

  return (
    <div className="space-y-3">
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', lg: 'center' },
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0,
            alignItems: 'center',
            flex: { xs: '0 0 auto', lg: '1 1 auto' },
            minWidth: 0,
          }}
        >
            {([
              { key: 'completed' as const, label: 'Completed' },
              { key: 'in-progress' as const, label: 'In Progress' },
              { key: 'pending' as const, label: 'Pending' },
            ] as const).map((item) => {
              const isActive = activeFilter === item.key;

              return (
                  <Button
                    key={item.key}
                    variant="text"
                    onClick={() => setActiveFilter(item.key)}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: isActive ? '#1699c8' : '#667085',
                      bgcolor: 'transparent',
                      border: 'none',
                      borderBottom: isActive ? '2px solid #1699c8' : '2px solid transparent',
                      py: 1,
                      px: { xs: 1, sm: 1.5 },
                      mr: { xs: 1, sm: 2 },
                      marginBottom: '-1px',
                      borderRadius: 0,
                      minHeight: 44,
                      '&:hover': { bgcolor: 'transparent', color: isActive ? '#1699c8' : '#475467' },
                    }}
                  >
                    <Box component="span" sx={{ mr: 0.75 }}>
                      {item.label}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 24,
                        height: 22,
                        px: 1,
                        borderRadius: '999px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '13px', sm: '14px' },
                        bgcolor: isActive ? '#47a9d1' : '#f1f5f9',
                        color: isActive ? '#fff' : '#64748b',
                      }}
                    >
                      {filterCounts[item.key]}
                    </Box>
                  </Button>
              );
            })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.5,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate('/questionnaires')}
            sx={{
              flexShrink: 0,
              height: { xs: 36, sm: 34 },
              px: 1.75,
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '12px', sm: '13px' },
              fontWeight: 600,
              bgcolor: '#25a8da',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: '#1498ca',
                boxShadow: 'none',
              },
            }}
          >
            Add Questionnaire
          </Button>
          <TextField
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search"
            size="small"
            sx={{
              width: { xs: '100%', sm: 180, md: 200 },
              maxWidth: '100%',
              '& .MuiOutlinedInput-root': {
                height: { xs: 32, sm: 34 },
                borderRadius: '999px',
                bgcolor: '#f6f7f9',
                pl: { xs: 0.25, sm: 0.5 },
                pr: { xs: 0.375, sm: 0.5 },
                '& fieldset': { borderColor: '#eaecf0' },
                '&:hover fieldset': { borderColor: '#dde1e7' },
                '&.Mui-focused fieldset': { borderColor: '#d5dae2' },
              },
              '& .MuiInputBase-input': {
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '12px', sm: '13px' },
                color: '#8c9199',
                py: 0,
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#a8acb3',
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: '#b0b3b8', fontSize: { xs: 18, sm: 20 } }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {filteredQuestionnaireCards.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.875rem',
              color: '#667085',
            }}
          >
            {emptyQuestionnaireMessage}
          </Typography>
          {questionnaireCards.length === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate('/questionnaires')}
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                bgcolor: '#25a8da',
                borderRadius: '10px',
                px: 2.5,
                py: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
              }}
            >
              Add Questionnaire
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredQuestionnaireCards.map(({ questionnaire, card }) => {
            const isExportingThisQuestionnaire = exportingInstanceIds.has(questionnaire.instanceId);
            const canViewQuestionnaire = questionnaire.status === 'completed';

            return (
              <QuestionnaireListRow
                key={questionnaire.instanceId}
                card={card}
                onPreview={() => openQuestionnaireView(navigate, questionnaire, clientId, manualFillClientName)}
                previewLabel="View"
                showPreviewButton={canViewQuestionnaire}
                enablePreviewClick={canViewQuestionnaire}
                onResponses={() => {
                  void copyQuestionnaireLink(questionnaire);
                }}
                responsesLabel="Copy Link"
                responsesButtonVariant="outlined"
                onFillManually={() => setManualFillQuestionnaire(questionnaire)}
                showFillManuallyButton={activeFilter !== 'completed'}
                disableResponses={questionnaire.status === 'completed'}
                showResponsesButton={activeFilter !== 'completed'}
                assignLabel={isExportingThisQuestionnaire ? 'Exporting...' : 'Export'}
                assignButtonVariant="outlined"
                assignEndIcon={
                  isExportingThisQuestionnaire ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: 'currentColor' }} />
                  ) : (
                    <FileDownloadOutlinedIcon sx={{ fontSize: 18, color: 'currentColor' }} />
                  )
                }
                disableAssign={isExportingThisQuestionnaire}
                onAssign={() => {
                  void handleExportQuestionnaire(questionnaire);
                }}
                showAssignButton
                showStatusChip={
                  questionnaire.status === 'pending' ||
                  questionnaire.status === 'in_progress' ||
                  questionnaire.status === 'completed'
                }
                titleStatusChip={
                  questionnaire.status === 'pending'
                    ? {
                        label: 'Not started',
                        bgColor: 'transparent',
                        color: '#64748b',
                        dotColor: '#94a3b8',
                      }
                    : questionnaire.status === 'in_progress'
                      ? {
                          label: 'In Progress',
                          bgColor: 'transparent',
                          color: '#64748b',
                          dotColor: 'transparent',
                        }
                      : questionnaire.status === 'completed'
                        ? {
                            label: 'Completed',
                            bgColor: 'transparent',
                            color: '#64748b',
                            dotColor: '#20c997',
                          }
                        : undefined
                }
                showQuestionMetric={typeof getQuestionnaireInstanceTotalQuestionCount(questionnaire) === 'number'}
                showEstimatedTimeMetric={typeof card.estimatedTimeMinutes === 'number'}
                showAssignedMetric={false}
                showResponsesMetric={false}
              />
            );
          })}
        </Box>
      )}

      <QuestionnaireManualFillDialog
        open={Boolean(manualFillQuestionnaire)}
        shareId={manualFillQuestionnaire?.instanceId ?? null}
        questionnaireTitle={
          manualFillQuestionnaire ? getQuestionnaireTitle(manualFillQuestionnaire) || 'Questionnaire' : 'Questionnaire'
        }
        clientName={manualFillClientName}
        assignedClientProfile={
          clientDetails?.client
            ? sharedAssignedClientFromClientProfile(clientDetails.client)
            : undefined
        }
        onClose={() => setManualFillQuestionnaire(null)}
        onDraftSaved={handleManualFillDraftSaved}
        onSubmitted={handleManualFillSubmitted}
      />
    </div>
  );
};
